import dayjs from 'dayjs';
import dayjsDuration from 'dayjs/plugin/duration';
import { i18next } from '@frontend/i18n';

dayjs.extend(dayjsDuration);

export function formatDuration(duration: number, options?: { showSeconds?: boolean }): string {
  const showSeconds = options?.showSeconds ?? true;

  if (duration < 60000) {
    return showSeconds ? `${Math.floor(duration / 1000)}s` : i18next.t('Less than a minute');
  }

  if (duration < 3600000) {
    return showSeconds
      ? dayjs.duration(duration, 'ms').format('m[m] s[s]')
      : dayjs.duration(duration, 'ms').format('m[m]');
  }

  if (duration < 86400000) {
    return showSeconds
      ? dayjs.duration(duration, 'ms').format('H[h] m[m] s[s]')
      : dayjs.duration(duration, 'ms').format('H[h] m[m]');
  }

  return showSeconds
    ? dayjs.duration(duration, 'ms').format('D[d] H[h] m[m] s[s]')
    : dayjs.duration(duration, 'ms').format('D[d] H[h] m[m]');
}

export const getSearchedNameParts = (name: string, searchTerm: string) => {
  const index = name.toLowerCase().indexOf(searchTerm.toLowerCase());
  if (index === -1) return [name];

  return [name.slice(0, index), name.slice(index, index + searchTerm.length), name.slice(index + searchTerm.length)];
};

/**
 * Why do we need this?
 *
 * Team Chat does not have an open API where we can programmatically open a chat with a specific name or userId.
 * To work around this, we simulate typing into the chat name input field to open the desired chat.
 *
 * There is the ability to open a static conversation if we know the channelId.
 * That requires that the user must have had a pre-existing conversation with that user. (We do incorporate that flow if that is the case.)
 */
export const simulateTypingIntoChatNameInput = (name: string) => {
  const input = document.querySelector('input[name=channel-search]') as HTMLInputElement;

  if (input) {
    input.focus();

    // Simulate backspace to clear any existing value
    input.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: 'Backspace',
        code: 'Backspace',
        keyCode: 8,
        which: 8,
        bubbles: true,
        cancelable: true,
      })
    );
    input.value = '';
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(
      new KeyboardEvent('keyup', {
        key: 'Backspace',
        code: 'Backspace',
        keyCode: 8,
        which: 8,
        bubbles: true,
        cancelable: true,
      })
    );

    // Simulate down arrow key
    input.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: 'ArrowDown',
        code: 'ArrowDown',
        keyCode: 40,
        which: 40,
        bubbles: true,
        cancelable: true,
      })
    );
    input.dispatchEvent(
      new KeyboardEvent('keyup', {
        key: 'ArrowDown',
        code: 'ArrowDown',
        keyCode: 40,
        which: 40,
        bubbles: true,
        cancelable: true,
      })
    );

    // Simulate typing each letter with a small delay between each
    let currentValue = '';
    name.split('').forEach((char, index) => {
      currentValue += char;

      // Set the value
      input.value = currentValue;

      // Dispatch keydown event for this character
      input.dispatchEvent(
        new KeyboardEvent('keydown', {
          key: char,
          code: `Key${char.toUpperCase()}`,
          bubbles: true,
          cancelable: true,
        })
      );

      // Dispatch input event to trigger React's onChange
      input.dispatchEvent(new Event('input', { bubbles: true }));

      // Dispatch keyup event for this character
      input.dispatchEvent(
        new KeyboardEvent('keyup', {
          key: char,
          code: `Key${char.toUpperCase()}`,
          bubbles: true,
          cancelable: true,
        })
      );

      // If this is the last character, dispatch Enter key
      if (index === name.length - 1) {
        setTimeout(() => {
          input.dispatchEvent(
            new KeyboardEvent('keydown', {
              key: 'Enter',
              code: 'Enter',
              keyCode: 13,
              which: 13,
              bubbles: true,
              cancelable: true,
            })
          );
          input.dispatchEvent(
            new KeyboardEvent('keyup', {
              key: 'Enter',
              code: 'Enter',
              keyCode: 13,
              which: 13,
              bubbles: true,
              cancelable: true,
            })
          );
        }, 50);
      }
    });
  }
};
